import * as React from 'react';
import { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface CollapsibleSectionProps {
  title: string;
  icon?: ReactNode;
  defaultOpen?: boolean;
  open?: boolean; // Controlled mode
  onOpenChange?: (open: boolean) => void; // Controlled mode
  summary?: ReactNode; // Shows when collapsed
  /** Rendered in the header row, outside the collapse trigger. Stop propagation inside to prevent toggling. */
  headerActions?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
}

export function CollapsibleSection({
  title,
  icon,
  defaultOpen = true,
  open,
  onOpenChange,
  summary,
  headerActions,
  children,
  className,
  testId,
}: CollapsibleSectionProps) {
  // Track open state for summary visibility (works for both controlled and uncontrolled)
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isOpen = open !== undefined ? open : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    setInternalOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  return (
    <Collapsible
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      className={cn('bg-white rounded-lg border border-gray-200', className)}
      data-testid={testId}
    >
      <CollapsibleTrigger
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <div className="flex items-center gap-3">
          {icon}
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
        <div className="flex items-center gap-4">
          {/* Show summary when collapsed */}
          {!isOpen && summary && (
            <div className="text-sm text-gray-600" data-testid={testId ? `${testId}-summary` : undefined}>
              {summary}
            </div>
          )}
          {/* Stop-propagation wrapper so clicking headerActions doesn't toggle the section */}
          {headerActions && (
            <div
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              data-testid={testId ? `${testId}-header-actions` : undefined}
            >
              {headerActions}
            </div>
          )}
          <ChevronDown
            className={cn(
              'w-5 h-5 text-gray-500 transition-transform duration-200',
              !isOpen && '-rotate-90'
            )}
            data-testid={testId ? `${testId}-chevron` : undefined}
          />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className="border-t border-gray-100">
        <div className="p-4">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
